import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, RefreshCw, Facebook, Settings } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { platforms } from '../lib/platforms';
import { fetchPlatformMetrics, aggregateMetrics } from '../lib/platformData';
import { fetchMetaAdsData } from '../lib/platforms/meta';
import PlatformMetrics from '../components/PlatformMetrics';
import type { Platform, AnalyticsMetric } from '../lib/types';

interface ConnectedAccount {
  account_id: string;
  name: string;
  business_name?: string;
  status: number;
  currency: string;
  timezone: string;
}

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [platformData, setPlatformData] = useState<Record<string, AnalyticsMetric[]>>({});
  const [refreshing, setRefreshing] = useState(false);
  const [connectedAccount, setConnectedAccount] = useState<ConnectedAccount | null>(null);
  const navigate = useNavigate();

  const loadPlatformData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get user's connected platforms
      const { data: connections, error: connectionsError } = await supabase
        .from('platform_connections')
        .select('platform_id, platform_type, last_sync, credentials')
        .eq('is_active', true);

      if (connectionsError) throw connectionsError;

      if (!connections?.length) {
        setLoading(false);
        return;
      }

      console.log('Found connected platforms:', connections);

      // Extract Meta Ads account
      const metaAdsConnection = connections.find(c => c.platform_id === 'meta-ads');
      if (metaAdsConnection?.credentials?.available_accounts?.[0]) {
        // Only use the first (selected) account
        const account = metaAdsConnection.credentials.available_accounts[0];
        setConnectedAccount({
          account_id: account.account_id,
          name: account.name,
          business_name: account.business_name,
          status: account.account_status || account.status,
          currency: account.currency,
          timezone: account.timezone || account.timezone_name
        });
      }

      // For Meta Ads, fetch fresh data first
      if (metaAdsConnection) {
        try {
          console.log('Fetching fresh Meta Ads data');
          await fetchMetaAdsData();
        } catch (error) {
          console.error('Error fetching fresh Meta Ads data:', error);
          // Continue to fetch existing metrics even if fresh fetch fails
        }
      }

      // Fetch metrics for each connected platform
      const metricsData: Record<string, AnalyticsMetric[]> = {};
      await Promise.all(
        connections.map(async ({ platform_id }) => {
          try {
            const platform = platforms.find(p => p.id === platform_id);
            if (!platform) {
              console.warn(`Platform configuration not found for ${platform_id}`);
              return;
            }

            console.log(`Fetching metrics for ${platform_id}`);
            const metrics = await fetchPlatformMetrics(platform_id);
            if (metrics && metrics.length > 0) {
              metricsData[platform_id] = aggregateMetrics(metrics, platform);
              console.log(`Metrics aggregated for ${platform_id}:`, metricsData[platform_id]);
            }
          } catch (error) {
            console.error(`Error fetching metrics for ${platform_id}:`, error);
          }
        })
      );

      setPlatformData(metricsData);
    } catch (error) {
      console.error('Error loading platform data:', error);
      setError('Failed to load analytics data. Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlatformData();
  }, []);

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await loadPlatformData();
    } finally {
      setRefreshing(false);
    }
  };

  const getAccountStatusLabel = (status: number) => {
    switch (status) {
      case 1:
        return { label: 'Active', class: 'bg-green-500/10 text-green-400 border-green-500/20' };
      case 2:
        return { label: 'Disabled', class: 'bg-red-500/10 text-red-400 border-red-500/20' };
      case 3:
        return { label: 'Unsettled', class: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' };
      default:
        return { label: 'Unknown', class: 'bg-gray-500/10 text-gray-400 border-gray-500/20' };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500 mb-4" />
          <p className="text-gray-400">Loading analytics data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Error Loading Data</h3>
          <p className="text-gray-400 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-all"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const connectedPlatforms = Object.keys(platformData);

  if (connectedPlatforms.length === 0 && !connectedAccount) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-white mb-4">No Platforms Connected</h2>
        <p className="text-gray-400 mb-8">Connect your first platform to start seeing analytics data</p>
        <button
          onClick={() => navigate('/dashboard/integrations')}
          className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-all transform hover:scale-105"
        >
          Connect Platforms
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-12">
      <div className="flex justify-end">
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center px-4 py-2 text-sm font-medium text-blue-400 hover:text-blue-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing...' : 'Refresh Data'}
        </button>
      </div>

      {/* Connected Meta Ad Account */}
      {connectedAccount && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-white">Connected Meta Ad Account</h2>
            <Facebook className="h-6 w-6 text-blue-400" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div
              className="bg-[#151632]/50 backdrop-blur-sm p-6 rounded-xl border border-[#1E2048] hover:border-purple-500/25 transition-all duration-300"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-white">{connectedAccount.name}</h3>
                  {connectedAccount.business_name && (
                    <p className="text-gray-400 text-sm mt-1">{connectedAccount.business_name}</p>
                  )}
                </div>
                <button className="text-gray-400 hover:text-blue-400 transition-colors">
                  <Settings className="h-5 w-5" />
                </button>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-gray-400">
                  Account ID: {connectedAccount.account_id}
                </p>
                <p className="text-sm text-gray-400">
                  Currency: {connectedAccount.currency}
                </p>
                <p className="text-sm text-gray-400">
                  Timezone: {connectedAccount.timezone}
                </p>
                <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getAccountStatusLabel(connectedAccount.status).class}`}>
                  {getAccountStatusLabel(connectedAccount.status).label}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Platform Metrics */}
      {connectedPlatforms.map(platformId => {
        const platform = platforms.find(p => p.id === platformId);
        if (!platform) return null;

        return (
          <div key={platformId} className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-white">{platform.name}</h2>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                platform.category === 'advertising'
                  ? 'bg-purple-500/10 text-purple-400 border border-purple-500/20'
                  : 'bg-green-500/10 text-green-400 border border-green-500/20'
              }`}>
                {platform.category === 'advertising' ? 'Advertising' : 'Sales'}
              </span>
            </div>
            <PlatformMetrics metrics={platformData[platformId]} />
          </div>
        );
      })}
    </div>
  );
};

export default Dashboard;